import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from 'vue';
import { useDropZone } from '@vueuse/core';
import { useRouter } from 'vue-router/composables';
import { storeToRefs } from 'pinia';
import BasicButton from '@/ui-components/BasicButton.vue';
import { useMutation } from '@/helpers/composables/useApollo';
import { AccountModal } from '@/lazy-load-components';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import {
	ImportTVTimeDocument,
	type ImportTVTimeMutation,
	type ImportTVTimeMutationVariables,
} from '@/graphql/mutation/ImportTVTime.mutation';

type ImportState = 'idle' | 'matched' | 'success' | 'error';


export default /*#__PURE__*/_defineComponent({
  __name: 'TvtimeImporter',
  setup(__props) {

const router = useRouter();
const { isLoggedIn } = storeToRefs(useUserStore());

const state = ref<ImportState>('idle');
const fileInputRef = ref<HTMLInputElement | null>(null);
const dropzoneRef = ref<HTMLElement | null>(null);
const selectedFile = ref<File | null>(null);

const { isOverDropZone } = useDropZone(dropzoneRef, {
	dataTypes: ['application/zip', 'application/x-zip-compressed'],
	onDrop(files) {
		const file = files?.[0] ?? null;
		if (!file) return;
		selectedFile.value = file;
		state.value = 'idle';
	},
});
const isMatching = ref(false);
const isImporting = ref(false);
const matchedCount = ref(0);
const importedCount = ref(0);

const { mutate } = useMutation<ImportTVTimeMutation, ImportTVTimeMutationVariables>(ImportTVTimeDocument, {
	throws: 'always',
});

function onFileChange(event: Event) {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0] ?? null;
	if (!file) return;
	selectedFile.value = file;
	state.value = 'idle';
}

function clearFile() {
	selectedFile.value = null;
	state.value = 'idle';
	if (fileInputRef.value) {
		fileInputRef.value.value = '';
	}
}

async function createNow() {
	if (!selectedFile.value) return;
	if (isLoggedIn.value) {
		await runImport();
	} else {
		await matchTitles();
	}
}

async function matchTitles() {
	isMatching.value = true;
	try {
		const result = await mutate({ file: selectedFile.value!, readOnly: true });
		const payload = result?.data?.importTVTimeLists;
		matchedCount.value = (payload?.watchlistMatched ?? 0) + (payload?.seenlistMatched ?? 0);
		state.value = 'matched';
	} catch {
		state.value = 'error';
	} finally {
		isMatching.value = false;
	}
}

async function runImport() {
	if (!selectedFile.value || isImporting.value) return;
	isImporting.value = true;
	try {
		const result = await mutate({ file: selectedFile.value, readOnly: false });
		const payload = result?.data?.importTVTimeLists;
		importedCount.value = (payload?.watchlistAdded ?? 0) + (payload?.seenlistAdded ?? 0);
		state.value = 'success';
	} catch {
		state.value = 'error';
	} finally {
		isImporting.value = false;
	}
}

function retry() {
	state.value = 'idle';
	selectedFile.value = null;
	matchedCount.value = 0;
	isMatching.value = false;
	isImporting.value = false;
	if (fileInputRef.value) {
		fileInputRef.value.value = '';
	}
}

function openSignup() {
	ModalHelper.openModal(AccountModal, { closable: true }, { cssClass: 'account-modal' }, 'TvtimeImporter');
}

watch(isLoggedIn, newVal => {
	if (newVal && state.value === 'matched') {
		runImport();
	}
});

function goToWatchlist() {
	router.push({ name: 'app.lists.my-lists' });
}

return { __sfc: true,router, isLoggedIn, state, fileInputRef, dropzoneRef, selectedFile, isOverDropZone, isMatching, isImporting, matchedCount, importedCount, mutate, onFileChange, clearFile, createNow, matchTitles, runImport, retry, openSignup, goToWatchlist, BasicButton }
}

})